import {
  Box,
  HStack,
  Text,
  Icon,
  Tooltip,
  Badge,
  Progress,
  VStack,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react'
import { FiZap } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useCreditBalance } from '../../api/api_hooks/credits'

interface CreditBadgeProps {
  showDetails?: boolean
  variant?: 'compact' | 'full'
}

export default function CreditBadge({ showDetails = true, variant = 'compact' }: CreditBadgeProps) {
  const navigate = useNavigate()
  const { data, isLoading } = useCreditBalance()

  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.700', 'gray.200')

  if (isLoading) {
    return <Skeleton height="40px" width={variant === 'compact' ? '120px' : '200px'} borderRadius="md" />
  }

  if (!data?.balance) {
    return null
  }

  const { totalCredits, remainingCredits, usedCredits } = data.balance
  const isUnlimited = totalCredits >= 999999
  const percentage = isUnlimited ? 100 : (remainingCredits / totalCredits) * 100
  const isLow = percentage < 20 && !isUnlimited

  const getColor = () => {
    if (isUnlimited) return 'purple'
    if (isLow) return 'red'
    if (percentage < 50) return 'orange'
    return 'green'
  }

  const color = getColor()

  if (variant === 'compact') {
    // Color tokens for the active palette — used for the glow ring, icon
    // accent and progress bar. Compact form is meant to sit on the dark
    // purple sidebar, so the surface is a translucent glass tile, not the
    // plain white card the old one used (which overflowed the collapsed
    // 96 px sidebar). maxW caps it so it never spills past the rail.
    const palette = {
      red:    { glow: 'rgba(248,113,113,0.50)', accent: '#fca5a5', dot: '#ef4444' },
      orange: { glow: 'rgba(251,146,60,0.45)',  accent: '#fdba74', dot: '#f97316' },
      green:  { glow: 'rgba(74,222,128,0.40)',  accent: '#86efac', dot: '#22c55e' },
      purple: { glow: 'rgba(196,181,253,0.45)', accent: '#ddd6fe', dot: '#a855f7' },
    }[color]

    return (
      <Tooltip
        label={
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="bold">
              {isUnlimited ? 'Crédits illimités' : `${remainingCredits} / ${totalCredits} crédits`}
            </Text>
            {!isUnlimited && (
              <>
                <Progress value={percentage} size="sm" colorScheme={color} borderRadius="full" />
                <Text fontSize="xs">{usedCredits} crédits utilisés ce mois-ci</Text>
              </>
            )}
            <Text fontSize="xs" color="gray.400">Cliquer pour gérer le plan</Text>
          </VStack>
        }
        hasArrow
        placement="right"
        bg="gray.900"
        color="white"
        borderRadius="lg"
        px={3}
        py={2}
      >
        <Box
          cursor="pointer"
          onClick={() => navigate('/change-plan')}
          position="relative"
          minW="64px"
          maxW="80px"
          px={3}
          py={1.5}
          borderRadius="xl"
          bg="rgba(255, 255, 255, 0.06)"
          backdropFilter="blur(8px)"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.10)"
          boxShadow={`0 0 0 1px rgba(255,255,255,0.04), 0 4px 14px -4px ${palette.glow}, inset 0 1px 0 rgba(255,255,255,0.06)`}
          transition="all 0.22s cubic-bezier(0.4, 0, 0.2, 1)"
          _hover={{
            transform: 'translateY(-1px)',
            bg: 'rgba(255, 255, 255, 0.10)',
            borderColor: 'rgba(255, 255, 255, 0.18)',
            boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 8px 22px -6px ${palette.glow}, inset 0 1px 0 rgba(255,255,255,0.10)`,
          }}
          _active={{ transform: 'translateY(0)' }}
        >
          <HStack spacing={1.5} justify="center" align="center">
            <Box position="relative" display="grid" sx={{ placeItems: 'center' }} w="18px" h="18px">
              <Icon as={FiZap} color={palette.accent} boxSize="14px" />
              {/* Soft glow halo behind the bolt icon */}
              <Box
                position="absolute"
                inset="-3px"
                borderRadius="full"
                bg={palette.glow}
                filter="blur(6px)"
                opacity={0.6}
                pointerEvents="none"
              />
            </Box>
            <Text fontSize="sm" fontWeight="bold" color="white" lineHeight="1" letterSpacing="-0.01em">
              {isUnlimited ? '∞' : remainingCredits}
            </Text>
            {isLow && !isUnlimited && (
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg={palette.dot}
                boxShadow={`0 0 6px ${palette.dot}`}
                sx={{
                  animation: 'creditPulse 1.6s ease-in-out infinite',
                  '@keyframes creditPulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%':      { opacity: 0.5, transform: 'scale(0.85)' },
                  },
                }}
              />
            )}
          </HStack>
        </Box>
      </Tooltip>
    )
  }

  // Full variant
  return (
    <Box
      cursor="pointer"
      onClick={() => navigate('/change-plan')}
      px={4}
      py={3}
      borderRadius="lg"
      bg={bgColor}
      borderWidth="1px"
      borderColor={isLow ? 'red.300' : borderColor}
      _hover={{
        borderColor: `${color}.500`,
        transform: 'translateY(-2px)',
        shadow: 'lg',
      }}
      transition="all 0.2s"
      minW="200px"
    >
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Icon as={FiZap} color={`${color}.500`} boxSize={5} />
            <Text fontSize="sm" fontWeight="bold" color={textColor}>
              Credits
            </Text>
          </HStack>
          {isLow && !isUnlimited && (
            <Badge colorScheme="red" fontSize="xs">
              Low
            </Badge>
          )}
        </HStack>

        {isUnlimited ? (
          <Text fontSize="2xl" fontWeight="bold" color={`${color}.500`}>
            Unlimited
          </Text>
        ) : (
          <>
            <HStack justify="space-between">
              <Text fontSize="2xl" fontWeight="bold" color={`${color}.500`}>
                {remainingCredits}
              </Text>
              <Text fontSize="sm" color="gray.500">
                / {totalCredits}
              </Text>
            </HStack>
            <Progress
              value={percentage}
              size="sm"
              colorScheme={color}
              borderRadius="full"
            />
            <Text fontSize="xs" color="gray.500">
              {usedCredits} used this month
            </Text>
          </>
        )}
      </VStack>
    </Box>
  )
}
